import { useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { isNativeApp, isAndroidApp } from "@/lib/platform";
import { faqLd, howToLd, softwareAppLd, organizationLd, websiteLd } from "@/lib/seo-config";
import {
  Sparkles,
  Wand2,
  Heart,
  Camera,
  Check,
  Download,
  Shield,
  Zap,
  ArrowRight,
  Star,
  ScanFace,
  Waves,
  CheckCircle2,
  Cpu,
  Lock,
  Cloud,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useRegion } from "@/hooks/useRegion";

import { BeforeAfter } from "@/components/BeforeAfter";
import { AutoBeforeAfter } from "@/components/AutoBeforeAfter";
import { Reveal } from "@/components/RevealOnScroll";
import howtoPerson from "@/assets/howto/step-person.webp";
import howtoOutfit from "@/assets/howto/step-outfit.webp";
import howtoResult from "@/assets/howto/step-result.webp";
import { Marquee } from "@/components/ui/marquee";
import { GlobePolaroids, type PolaroidMarker } from "@/components/ui/cobe-globe-polaroids";
import { LiveViewers } from "@/components/LiveViewers";
import showcaseBefore from "@/assets/showcase/before.webp";
import showcaseA from "@/assets/showcase/after-a.webp";
import showcaseB from "@/assets/showcase/after-b.webp";
import showcaseC from "@/assets/showcase/after-c.webp";
import before1 from "@/assets/before-1.webp";
import after1 from "@/assets/after-1.webp";
import before2 from "@/assets/before-2.webp";
import after2 from "@/assets/after-2.webp";
import before3 from "@/assets/before-3.webp";
import after3 from "@/assets/after-3.webp";
import before4 from "@/assets/before-4.webp";
import after4 from "@/assets/after-4.webp";
import before5 from "@/assets/before-5.webp";
import after5 from "@/assets/after-5.webp";
import before6 from "@/assets/before-6.webp";
import after6 from "@/assets/after-6.webp";
import before7 from "@/assets/before-7.webp";
import after7 from "@/assets/after-7.webp";

const examples = [
  { before: showcaseBefore, after: showcaseA, label: "Selfie → Cinematic vibe", tag: "Vibe" },
  { before: showcaseBefore, after: showcaseB, label: "Selfie → Styled scene", tag: "Vibe" },
  { before: showcaseBefore, after: showcaseC, label: "Selfie → Editorial look", tag: "Vibe" },
  { before: before1, after: after1, label: "Everyday tee → Statement top", tag: "Top" },
  { before: before2, after: after2, label: "Plain look → Styled accessory", tag: "Accessory" },
  { before: before3, after: after3, label: "Casual fit → Polished outfit", tag: "Outfit" },
  { before: before4, after: after4, label: "Basic → Trendy upgrade", tag: "Style" },
  { before: before5, after: after5, label: "Daywear → Sharp all-black party", tag: "Vibe · Party" },
  { before: before6, after: after6, label: "Studio look → Vibe scene", tag: "Vibe" },
  { before: before7, after: after7, label: "Plain photo → Cinematic vibe", tag: "Vibe" },
];

// Real FitAura try-ons orbiting the globe — proves it works for people everywhere.
const GLOBE_POLAROIDS: PolaroidMarker[] = [
  { id: "p-mumbai",  location: [19.07,  72.88],  image: after5, caption: "Mumbai",     rotate: -6 },
  { id: "p-nyc",     location: [40.71, -74.01],  image: after1, caption: "New York",   rotate:  4 },
  { id: "p-tokyo",   location: [35.68, 139.65],  image: after3, caption: "Tokyo",      rotate: -3 },
  { id: "p-paris",   location: [48.86,   2.35],  image: after6, caption: "Paris",      rotate:  5 },
  { id: "p-london",  location: [51.51,  -0.13],  image: after2, caption: "London",     rotate: -4 },
  { id: "p-sydney",  location: [-33.87, 151.21], image: after4, caption: "Sydney",     rotate:  6 },
  { id: "p-dubai",   location: [25.20,  55.27],  image: after7, caption: "Dubai",      rotate: -5 },
  { id: "p-berlin",  location: [52.52,  13.40],  image: after5, caption: "Berlin",     rotate:  3 },
];

const TESTIMONIALS = [
  { name: "Aanya M.",   role: "Stylist · Mumbai",        rating: 5, quote: "I shop 10× faster now. I drop a link, see it on me, and instantly know if it's an aura match." },
  { name: "Jordan T.",  role: "Creator · NYC",           rating: 5, quote: "The fits are scary realistic. Folds, fabric, lighting — it actually looks like the photo I would've taken." },
  { name: "Priya S.",   role: "Bride-to-be · Bangalore", rating: 5, quote: "Tried 40 lehengas in one evening without leaving my couch. FitAura literally saved my wedding planning." },
  { name: "Marcus L.",  role: "Photographer · Berlin",   rating: 5, quote: "I use it to pre-visualize editorial shoots. The styling instincts of FitAura are unreal." },
  { name: "Sara K.",    role: "Founder · London",        rating: 5, quote: "Returns dropped 60% on my Shopify store after I added FitAura try-on previews. Game changer." },
  { name: "Riya V.",    role: "Student · Pune",          rating: 5, quote: "Finally figured out my body type and color palette without spending on a stylist. Obsessed." },
  { name: "Kenji A.",   role: "Designer · Tokyo",        rating: 5, quote: "The AI nails Japanese street styling — silhouettes, layering, proportions. Better than my Pinterest board." },
  { name: "Layla H.",   role: "Influencer · Dubai",      rating: 5, quote: "I shoot 5 outfits before breakfast now. My content output literally tripled this month." },
  { name: "Diego R.",   role: "Groom · Madrid",          rating: 5, quote: "Picked my entire wedding wardrobe with FitAura. My fiancée said it was the best fit I've ever worn." },
];

const CLOUD_FEATURE = "📱 Auto-saved to your phone (Android)";

const pricingPacks = [
  {
    label: "Starter",
    credits: 1,
    priceInr: "Free",
    priceUsd: "Free",
    perCreditInr: "1 free credit",
    perCreditUsd: "1 free credit",
    sub: "Try the magic — no credit card needed.",
    badge: "Free forever",
    features: [
      "1 AI try-on credit on signup",
      "Fit score & style match analysis",
      "HD download & social share",
    ],
    cta: "Start free",
  },
  {
    label: "First Try",
    credits: 5,
    priceInr: "₹49",
    priceUsd: "$2.99",
    perCreditInr: "₹9.8 / credit",
    perCreditUsd: "$0.60 / credit",
    sub: "Just enough to find your aura.",
    badge: "✨ Beginner pack",
    features: [
      "5 AI try-on credits",
      "Fit score & style match analysis",
      "HD download & social share",
      "Auto-save results to your phone",
    ],
    cta: "Get pack",
  },
  {
    label: "Small",
    credits: 15,
    priceInr: "₹99",
    priceUsd: "$4.99",
    perCreditInr: "₹6.6 / credit",
    perCreditUsd: "$0.33 / credit",
    sub: "Build a small capsule wardrobe.",
    features: [
      "15 AI try-on credits",
      "Full outfit generation (top + bottom + shoes)",
      "HD download & social share",
      "Auto-save results to your phone",
    ],
    cta: "Get pack",
  },
  {
    label: "Hot",
    credits: 22,
    priceInr: "₹149",
    priceUsd: "$6.99",
    perCreditInr: "₹6.8 / credit",
    perCreditUsd: "$0.32 / credit",
    sub: "Most picked starter for daily fits.",
    badge: "🔥 Trending",
    features: [
      "22 AI try-on credits",
      "Full outfit generation",
      "Style matching for any occasion",
      "HD download & social share",
      "Auto-save results to your phone",
      "Access to all outfit templates",
    ],
    cta: "Get pack",
  },
  {
    label: "Popular",
    credits: 38,
    priceInr: "₹249",
    priceUsd: "$9.99",
    perCreditInr: "just ₹6.5 / credit",
    perCreditUsd: "just $0.26 / credit",
    sub: "The sweet spot — try, refine, repeat.",
    badge: "⭐ Most Popular",
    features: [
      "38 AI try-on credits",
      CLOUD_FEATURE,
      "Full outfit generation + outfit looks",
      "Style matching for any occasion",
      "HD download & social share",
      "Auto-save results to your phone",
      "Customize outfit colors & styles",
      "Access to all outfit templates",
    ],
    cta: "Get pack",
    popular: true,
  },
  {
    label: "Best Value",
    credits: 80,
    priceInr: "₹499",
    priceUsd: "$17.99",
    perCreditInr: "₹6.2 / credit",
    perCreditUsd: "$0.22 / credit",
    sub: "For the daily stylist who can't stop.",
    badge: "💎 Best value",
    features: [
      "80 AI try-on credits",
      CLOUD_FEATURE,
      "Everything in Popular",
      "Priority generation queue (faster results)",
      "Advanced fit & body-type tuning",
      "Brand-aware product extraction",
      "Access to all outfit templates",
    ],
    cta: "Get pack",
  },
  {
    label: "Pro",
    credits: 160,
    priceInr: "₹999",
    priceUsd: "$29.99",
    perCreditInr: "₹6.2 / credit",
    perCreditUsd: "$0.19 / credit",
    sub: "Built for creators, resellers & stylists.",
    badge: "🚀 Pro",
    features: [
      "160 AI try-on credits",
      CLOUD_FEATURE,
      "Everything in Best Value",
      "Priority queue + early access to new models",
      "Bulk outfit generation",
      "Private WhatsApp support",
      "Email support within 24h",
      "Access to all outfit templates",
    ],
    cta: "Get pack",
  },
];

const scanProcess = [
  {
    icon: ScanFace,
    n: "01",
    t: "SNAP",
    d: "Capture your form. Our AI constructs a volumetric 3D avatar of your body instantly.",
  },
  {
    icon: Zap,
    n: "02",
    t: "LINK",
    d: "Drop any garment URL. We pull the fabric properties, weave density, and pattern data.",
  },
  {
    icon: Waves,
    n: "03",
    t: "DRAPE",
    d: "Nano Banana 2 engine simulates billions of fiber-to-skin interactions for perfect fit.",
  },
  {
    icon: CheckCircle2,
    n: "04",
    t: "STYLE",
    d: "Verify the look in cinematic environments. Share or shop with 100% confidence.",
  },
];

const HEADLINE_FONT = "'Space Grotesk', sans-serif";
const BODY_FONT = "'Manrope', sans-serif";

interface IndexProps {
  /**
   * When true, render as an in-app welcome tour:
   * - Skip the auto-redirect that bounces signed-in users to /app
   * - Show a sticky bottom "Continue to Setup" CTA
   * Used by the native Android welcome flow at /welcome.
   */
  embedded?: boolean;
  onContinue?: () => void;
}

const Index = ({ embedded = false, onContinue }: IndexProps = {}) => {
  const { session, loading } = useAuth();
  // Show INR pricing only for users whose device timezone is IST (Asia/Kolkata).
  // Everyone else sees USD. Native app handles its own pricing via Play Billing.
  const showInr = useRegion() === "in";
  

  useEffect(() => {
    // Inject Stitch fonts once
    const fontId = "stitch-fonts";
    if (!document.getElementById(fontId)) {
      const link = document.createElement("link");
      link.id = fontId;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;700&family=Manrope:wght@400;500;700;800&display=swap";
      document.head.appendChild(link);
    }

    // Inject glitch keyframes for hero before/after
    const glitchId = "fitaura-glitch-styles";
    if (!document.getElementById(glitchId)) {
      const style = document.createElement("style");
      style.id = glitchId;
      style.textContent = `
        @keyframes fa-glitch-red {
          0%, 92%, 100% { transform: translate(0,0); opacity: 0; }
          93% { transform: translate(-4px, 2px); opacity: 0.7; }
          95% { transform: translate(3px, -1px); opacity: 0.6; }
          97% { transform: translate(-2px, 1px); opacity: 0.8; }
          99% { transform: translate(1px, -2px); opacity: 0.5; }
        }
        @keyframes fa-glitch-cyan {
          0%, 92%, 100% { transform: translate(0,0); opacity: 0; }
          93% { transform: translate(4px, -2px); opacity: 0.7; }
          95% { transform: translate(-3px, 1px); opacity: 0.6; }
          97% { transform: translate(2px, -1px); opacity: 0.8; }
          99% { transform: translate(-1px, 2px); opacity: 0.5; }
        }
        @keyframes fa-glitch-clip {
          0%, 92%, 100% { clip-path: inset(0 0 0 0); }
          93% { clip-path: inset(20% 0 60% 0); }
          94% { clip-path: inset(70% 0 10% 0); }
          95% { clip-path: inset(40% 0 30% 0); }
          96% { clip-path: inset(10% 0 70% 0); }
          97% { clip-path: inset(55% 0 20% 0); }
          98% { clip-path: inset(30% 0 45% 0); }
          99% { clip-path: inset(0 0 0 0); }
        }
        @keyframes fa-glitch-flicker {
          0%, 92%, 100% { opacity: 0; }
          93%, 99% { opacity: 0.35; }
          95% { opacity: 0.15; }
          97% { opacity: 0.5; }
        }
        .fa-glitch-layer-r { animation: fa-glitch-red 5s steps(1) infinite, fa-glitch-clip 5s steps(1) infinite; mix-blend-mode: screen; }
        .fa-glitch-layer-c { animation: fa-glitch-cyan 5s steps(1) infinite, fa-glitch-clip 5s steps(1) infinite; mix-blend-mode: screen; }
        .fa-glitch-flicker { animation: fa-glitch-flicker 5s steps(1) infinite; }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // On native (Android/iOS), the marketing landing page is skipped entirely
  // for ALL users — including brand-new ones (no /welcome flash either).
  // We redirect synchronously on the very first render (don't wait for auth
  // to resolve) to avoid any flash of the web landing page on cold start:
  //   - signed-out (or unknown yet) → /auth
  //   - signed-in → /app  (RequireAuth on /app handles onboarding redirect)
  if (!embedded && isNativeApp()) {
    return <Navigate to={session ? "/app" : "/auth"} replace />;
  }
  if (!embedded && !loading && session) return <Navigate to="/app" replace />;


  return (
    <>
      <SEO jsonLd={[organizationLd, websiteLd, softwareAppLd, faqLd, howToLd]} />
    <div
      className="dark min-h-screen bg-[#0e0e11] text-[#e7e4ec] selection:bg-[#ac8aff]/30 selection:text-white"
      style={{ fontFamily: BODY_FONT }}
    >
      {/* Nav */}
      <header className="sticky top-0 z-30 border-b border-white/5 bg-[#0e0e11]/80 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-5 py-3 md:px-8">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-lg shadow-[0_0_24px_-4px_#ac8aff]">
              <img
                src="/app-icon-128.png"
                alt="FitAura AI logo"
                className="h-full w-full object-cover"
                width={36}
                height={36}
                loading="eager"
                decoding="async"
                fetchPriority="high"
              />
            </span>
            <span
              className="text-lg font-bold uppercase tracking-[0.18em] text-white"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              FitAura AI
            </span>
          </div>
          <nav
            className="hidden items-center gap-8 text-xs font-semibold uppercase tracking-[0.2em] text-[#acaab1] md:flex"
            style={{ fontFamily: HEADLINE_FONT }}
          >
            <a href="#examples" className="transition-colors hover:text-white">
              examples
            </a>
            <a href="#how" className="transition-colors hover:text-white">
              Scan Lab
            </a>
            <a href="#pricing" className="transition-colors hover:text-white">
              Pricing
            </a>
          </nav>
          <div className="flex items-center gap-2">
            <Button
              asChild
              variant="ghost"
              size="sm"
              className="hidden text-xs font-semibold uppercase tracking-[0.2em] text-[#acaab1] hover:bg-white/5 hover:text-white sm:inline-flex"
            >
              <Link to="/auth">Sign In</Link>
            </Button>
            <Button
              asChild
              size="sm"
              className="rounded-full bg-gradient-to-r from-[#ac8aff] to-[#8f60fa] text-xs font-bold uppercase tracking-[0.2em] text-[#0e0e11] shadow-[0_0_20px_-4px_#ac8aff] hover:from-[#bfa5ff] hover:to-[#ac8aff]"
            >
              <Link to="/auth">Try Free</Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        {/* Glow blobs */}
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute -top-40 left-1/4 h-[520px] w-[520px] rounded-full bg-[#ac8aff]/20 blur-[120px]" />
          <div className="absolute right-0 top-60 h-[380px] w-[380px] rounded-full bg-[#00daf3]/10 blur-[120px]" />
          <div
            className="absolute inset-0 opacity-[0.035]"
            style={{
              backgroundImage:
                "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
              backgroundSize: "60px 60px",
            }}
          />
          {/* Faded globe behind hero headline (desktop) */}
          <div className="absolute left-[-6%] top-1/2 hidden -translate-y-1/2 md:block lg:left-[-2%]">
            <div className="relative h-[640px] w-[640px] opacity-[0.30] [mask-image:radial-gradient(circle_at_center,black_45%,transparent_75%)]">
              <GlobePolaroids markers={GLOBE_POLAROIDS} speed={0.0025} />
            </div>
          </div>
          {/* Mobile: smaller globe centered behind hero text */}
          <div className="absolute left-1/2 top-[16%] -translate-x-1/2 md:hidden">
            <div className="relative h-[360px] w-[360px] opacity-[0.22] [mask-image:radial-gradient(circle_at_center,black_45%,transparent_75%)]">
              <GlobePolaroids markers={GLOBE_POLAROIDS} speed={0.0025} />
            </div>
          </div>
        </div>

        <div className="mx-auto grid w-full max-w-6xl gap-12 px-5 pb-20 pt-14 md:grid-cols-[1.05fr_1fr] md:gap-10 md:px-8 md:pb-28 md:pt-24">
          <div className="flex flex-col justify-center">
            <span
              className="inline-flex w-fit items-center gap-2 rounded-full border border-[#ac8aff]/30 bg-[#ac8aff]/5 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.25em] text-[#bfa5ff]"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#ac8aff]" />
              The Future of Fashion is Here
            </span>
            <h1
              className="mt-6 text-5xl font-bold uppercase leading-[0.95] tracking-tight text-white md:text-6xl lg:text-7xl"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              AI Virtual
              <br />
              <span className="bg-gradient-to-r from-[#ac8aff] via-[#bfa5ff] to-[#00daf3] bg-clip-text text-transparent">
                Fitting Room
              </span>
            </h1>
            <p className="mt-5 max-w-lg text-base text-[#acaab1] md:text-lg">
              Paste any product link from Amazon, Flipkart or Myntra and get a photo-real try-on with instant fit
              score. Or pick a Vibe — walk with a tiger, safari scenes, cinematic moods — and step into the look. All
              in seconds, powered by Google Nano Banana 2.
            </p>

            <div className="mt-8 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
              <Button
                asChild
                size="lg"
                className="h-12 rounded-full bg-gradient-to-r from-[#ac8aff] to-[#8f60fa] px-7 text-xs font-bold uppercase tracking-[0.25em] text-[#0e0e11] shadow-[0_0_30px_-6px_#ac8aff] hover:from-[#bfa5ff] hover:to-[#ac8aff]"
                style={{ fontFamily: HEADLINE_FONT }}
              >
                <Link to="/auth">
                  Start Scanning <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <span
                className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#acaab1]"
                style={{ fontFamily: HEADLINE_FONT }}
              >
                🚀 Instant 3D rendering
              </span>
            </div>

            <div
              className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#acaab1]"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              <span className="inline-flex items-center gap-1.5">
                <Check className="h-3.5 w-3.5 text-[#00daf3]" /> 1 free try-on
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Check className="h-3.5 w-3.5 text-[#00daf3]" /> No credit card
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Star className="h-3.5 w-3.5 fill-[#ff84a9] text-[#ff84a9]" /> Built for India
              </span>
            </div>
          </div>

          {/* Hero Visual */}
          <div className="relative mx-auto w-full max-w-md md:max-w-none">
            <div className="relative rounded-2xl border border-white/10 bg-white/[0.03] p-3 backdrop-blur-xl shadow-[0_0_60px_-15px_#ac8aff]">
              <div className="relative overflow-hidden rounded-xl">
                <BeforeAfter
                  beforeSrc={before5}
                  afterSrc={after5}
                  alt="Casual outfit transformed into a sharp all-black party look"
                />
                {/* RGB-split glitch layers */}
                <img
                  src={after5}
                  alt=""
                  aria-hidden
                  className="fa-glitch-layer-r pointer-events-none absolute inset-0 z-20 h-full w-full object-cover"
                  style={{ filter: "url(#fa-glitch-r-filter) drop-shadow(0 0 0 #ff003c)", mixBlendMode: "screen" }}
                />
                <img
                  src={after5}
                  alt=""
                  aria-hidden
                  className="fa-glitch-layer-c pointer-events-none absolute inset-0 z-20 h-full w-full object-cover"
                  style={{ filter: "drop-shadow(2px 0 0 #00daf3) drop-shadow(-2px 0 0 #ff003c)", mixBlendMode: "screen" }}
                />
                {/* Glitch flicker bands */}
                <div
                  className="fa-glitch-flicker pointer-events-none absolute inset-0 z-20"
                  style={{
                    backgroundImage:
                      "repeating-linear-gradient(0deg, rgba(172,138,255,0.15) 0px, rgba(172,138,255,0.15) 1px, transparent 1px, transparent 3px)",
                  }}
                />
                {/* Scan line */}
                <div className="pointer-events-none absolute inset-x-0 top-1/3 z-30 h-[2px] bg-gradient-to-r from-transparent via-[#ac8aff] to-transparent shadow-[0_0_30px_#ac8aff] animate-pulse" />
                {/* Corner brackets */}
                <div className="pointer-events-none absolute left-2 top-2 z-30 h-5 w-5 border-l-2 border-t-2 border-[#ac8aff]" />
                <div className="pointer-events-none absolute right-2 top-2 z-30 h-5 w-5 border-r-2 border-t-2 border-[#ac8aff]" />
                <div className="pointer-events-none absolute bottom-2 left-2 z-30 h-5 w-5 border-b-2 border-l-2 border-[#ac8aff]" />
                <div className="pointer-events-none absolute bottom-2 right-2 z-30 h-5 w-5 border-b-2 border-r-2 border-[#ac8aff]" />
              </div>

              {/* HUD chips */}
              <div
                className="mt-3 grid grid-cols-2 gap-2 text-[9px] font-semibold uppercase tracking-[0.18em] text-[#acaab1]"
                style={{ fontFamily: HEADLINE_FONT }}
              >
                <div className="flex items-center justify-between rounded-md border border-white/10 bg-black/40 px-2.5 py-1.5">
                  <span>SCAN_STATUS</span>
                  <span className="text-[#00daf3]">ACTIVE</span>
                </div>
                <div className="flex items-center justify-between rounded-md border border-white/10 bg-black/40 px-2.5 py-1.5">
                  <span>RESOLUTION</span>
                  <span className="text-[#00daf3]">4K_UHD</span>
                </div>
                <div className="flex items-center justify-between rounded-md border border-white/10 bg-black/40 px-2.5 py-1.5">
                  <span>GARMENT_FIT</span>
                  <span className="text-[#ac8aff]">99.8%</span>
                </div>
                <div className="flex items-center justify-between rounded-md border border-white/10 bg-black/40 px-2.5 py-1.5">
                  <span>FABRIC</span>
                  <span className="text-[#ff84a9]">SILK_TECH</span>
                </div>
              </div>
            </div>

            {/* Floating badge */}
            <div
              className="absolute -bottom-3 left-1/2 -translate-x-1/2 rounded-full border border-white/10 bg-[#19191d] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-white shadow-lg"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              ← Slide to Compare →
            </div>
          </div>
        </div>
      </section>

      {/* See it work — 3 step visual flow + auto before/after */}
      <section id="see-it-work" className="relative overflow-hidden border-t border-white/5 bg-[#0e0e11]">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute left-1/4 top-10 h-[360px] w-[360px] rounded-full bg-[#ac8aff]/15 blur-[120px]" />
          <div className="absolute right-0 bottom-10 h-[320px] w-[320px] rounded-full bg-[#00daf3]/10 blur-[120px]" />
        </div>

        <div className="mx-auto w-full max-w-6xl px-5 py-20 md:px-8 md:py-28">
          <Reveal className="text-center">
            <span
              className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#ac8aff]"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              — See it work
            </span>
            <h2
              className="mt-3 text-4xl font-bold uppercase tracking-tight text-white md:text-5xl"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              Three steps.
              <br />
              <span className="bg-gradient-to-r from-[#ac8aff] via-[#bfa5ff] to-[#00daf3] bg-clip-text text-transparent">
                One photo-real fit.
              </span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-sm text-[#acaab1] md:text-base">
              Watch a real FitAura try-on come together — your photo, any outfit, and a cinematic result in seconds.
            </p>
          </Reveal>

          {/* Visual 3-step flow */}
          <Reveal delay={0.1} className="mt-12">
            <div className="grid items-center gap-5 md:grid-cols-[1fr_auto_1fr_auto_1fr] md:gap-3">
              {[
                { src: howtoPerson, n: "01", t: "Your photo", d: "Upload a clear full-body shot." },
                { src: howtoOutfit, n: "02", t: "Any outfit", d: "Paste a link or upload a product image." },
                { src: howtoResult, n: "03", t: "Photo-real fit", d: "AI drapes it on you in seconds." },
              ].map((step, i) => (
                <div key={step.n} className="contents">
                  <div className="group relative">
                    <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-2 backdrop-blur-sm transition-all hover:border-[#ac8aff]/50 hover:shadow-[0_0_40px_-10px_#ac8aff]">
                      <div className="relative aspect-[3/4] w-full overflow-hidden rounded-xl bg-[#19191d]">
                        <img
                          src={step.src}
                          alt={step.t}
                          loading="lazy"
                          className="h-full w-full object-cover"
                        />
                        <span
                          className="absolute left-3 top-3 rounded-full bg-[#0e0e11]/80 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#bfa5ff] backdrop-blur"
                          style={{ fontFamily: HEADLINE_FONT }}
                        >
                          {step.n}
                        </span>
                      </div>
                    </div>
                    <div className="mt-4 px-1 text-center md:text-left">
                      <p
                        className="text-xs font-bold uppercase tracking-[0.25em] text-white"
                        style={{ fontFamily: HEADLINE_FONT }}
                      >
                        {step.t}
                      </p>
                      <p className="mt-1.5 text-sm text-[#acaab1]">{step.d}</p>
                    </div>
                  </div>

                  {i < 2 && (
                    <div className="flex items-center justify-center py-2 md:py-0">
                      <ArrowRight className="h-6 w-6 rotate-90 animate-pulse text-[#ac8aff] md:h-8 md:w-8 md:rotate-0" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Reveal>

          {/* Auto-animated before/after */}
          <Reveal delay={0.2} className="mt-16">
            <div className="grid items-center gap-8 md:grid-cols-[1fr_1.1fr] md:gap-12">
              <div>
                <span
                  className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#00daf3]"
                  style={{ fontFamily: HEADLINE_FONT }}
                >
                  — Live transformation
                </span>
                <h3
                  className="mt-3 text-3xl font-bold uppercase leading-tight tracking-tight text-white md:text-4xl"
                  style={{ fontFamily: HEADLINE_FONT }}
                >
                  Before. After.
                  <br />
                  <span className="bg-gradient-to-r from-[#ac8aff] to-[#00daf3] bg-clip-text text-transparent">
                    All you.
                  </span>
                </h3>
                <p className="mt-4 max-w-md text-sm text-[#acaab1] md:text-base">
                  Same face. Same pose. Same light. Only the outfit changes — every fold, every shadow rendered with
                  Nano Banana 2 precision.
                </p>
                <ul className="mt-6 space-y-2.5 text-sm text-white/90">
                  {[
                    "Your face & body, perfectly preserved",
                    "Realistic fabric drape & shadows",
                    "Cinematic background lighting",
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#00daf3]" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-7">
                  <Button
                    asChild
                    size="lg"
                    className="rounded-full bg-gradient-to-r from-[#ac8aff] to-[#8f60fa] text-xs font-bold uppercase tracking-[0.2em] text-[#0e0e11] shadow-[0_0_30px_-6px_#ac8aff] hover:from-[#bfa5ff] hover:to-[#ac8aff]"
                  >
                    <Link to="/auth">
                      Try it free <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>

              <div className="mx-auto w-full max-w-[420px]">
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-2 shadow-[0_0_60px_-20px_#ac8aff] backdrop-blur-sm">
                  <AutoBeforeAfter
                    beforeSrc={howtoPerson}
                    afterSrc={howtoResult}
                    alt="FitAura transformation"
                    durationSec={3.5}
                  />
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Real Results carousel */}
      <section id="examples" className="relative border-t border-white/5 bg-[#131317]">
        <div className="mx-auto w-full max-w-6xl px-5 py-20 md:px-8 md:py-28">
          <Reveal className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
            <div>
              <span
                className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#ac8aff]"
                style={{ fontFamily: HEADLINE_FONT }}
              >
                — Example
              </span>
              <h2
                className="mt-3 text-4xl font-bold uppercase tracking-tight text-white md:text-5xl"
                style={{ fontFamily: HEADLINE_FONT }}
              >
                Real Results
              </h2>
              <p className="mt-3 max-w-xl text-sm text-[#acaab1] md:text-base">
                Compare our AI output with physical reality. Perfection in every thread. Drag the slider on each
                example to see how FitAura AI styles real people with new outfits or Scene.
              </p>
            </div>
          </Reveal>

          {/* Auto-scrolling marquee rows. BeforeAfter sliders remain interactive —
              hovering pauses the row so users can drag the compare handle. */}
          <Reveal delay={0.1} className="mt-10 space-y-5">
            {[
              { items: examples.slice(0, Math.ceil(examples.length / 2)), reverse: false, dur: 55 },
              { items: examples.slice(Math.ceil(examples.length / 2)),    reverse: true,  dur: 65 },
            ].map((row, rowIdx) => (
              <div
                key={rowIdx}
                className="relative [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]"
              >
                <Marquee pauseOnHover reverse={row.reverse} durationSec={row.dur} gapPx={20}>
                  {row.items.map((ex, i) => (
                    <div
                      key={`${rowIdx}-${ex.label}`}
                      className="group/card w-[260px] shrink-0 space-y-3 sm:w-[280px]"
                    >
                      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-2 backdrop-blur-sm transition-all hover:border-[#ac8aff]/50 hover:shadow-[0_0_30px_-10px_#ac8aff]">
                        <BeforeAfter
                          beforeSrc={ex.before}
                          afterSrc={ex.after}
                          alt={ex.label}
                          afterObjectPosition={rowIdx === 0 && i === 0 ? "center 15%" : "center"}
                        />
                      </div>
                      <div className="flex items-center justify-between px-1">
                        <p
                          className="text-[11px] font-bold uppercase tracking-[0.15em] text-white"
                          style={{ fontFamily: HEADLINE_FONT }}
                        >
                          {ex.label}
                        </p>
                        <span
                          className="rounded-full border border-[#ac8aff]/30 bg-[#ac8aff]/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.15em] text-[#bfa5ff]"
                          style={{ fontFamily: HEADLINE_FONT }}
                        >
                          {ex.tag}
                        </span>
                      </div>
                    </div>
                  ))}
                </Marquee>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* Testimonials — infinite-scrolling reel + floating showcase images */}
      <section id="testimonials" className="relative overflow-hidden border-t border-white/5 bg-[#0e0e11]">
        {/* Ambient grid + spotlight + floating images */}
        <div className="pointer-events-none absolute inset-0">
          <div
            className="absolute inset-0 opacity-[0.08]"
            style={{
              backgroundImage:
                "linear-gradient(to right, #ac8aff 1px, transparent 1px), linear-gradient(to bottom, #ac8aff 1px, transparent 1px)",
              backgroundSize: "60px 60px",
            }}
          />
          <div className="absolute left-1/2 top-0 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-[#ac8aff]/25 blur-[120px]" />
          <div className="absolute -bottom-32 right-0 h-72 w-72 rounded-full bg-[#00daf3]/15 blur-3xl" />

          {[
            { src: after1,    cls: "left-[2%]  top-[6%]   h-36 w-28  -rotate-[8deg]  md:h-52 md:w-40", delay: "0s",   dur: "8s" },
            { src: showcaseA, cls: "right-[3%] top-[8%]   h-40 w-28   rotate-[10deg] md:h-56 md:w-40", delay: "1.2s", dur: "9s" },
            { src: after2,    cls: "left-[6%]  top-[38%]  h-32 w-24   rotate-[-5deg] md:h-44 md:w-32", delay: "0.4s", dur: "8.5s" },
            { src: after3,    cls: "left-[4%]  top-[78%]  h-36 w-26   rotate-[6deg]  md:h-48 md:w-36", delay: "2s",   dur: "7.5s" },
            { src: after5,    cls: "right-[5%] top-[72%]  h-36 w-28  -rotate-[12deg] md:h-52 md:w-40", delay: "0.6s", dur: "10s" },
            { src: after4,    cls: "right-[6%] top-[36%]  h-32 w-24   rotate-[8deg]  md:h-44 md:w-32", delay: "1.6s", dur: "9.2s" },
            { src: showcaseB, cls: "left-[40%] top-[1%]   hidden h-28 w-20  rotate-[4deg]  md:block md:h-36 md:w-28", delay: "1.8s", dur: "8.5s" },
            { src: showcaseC, cls: "right-[40%] bottom-[2%] hidden h-28 w-20 -rotate-[6deg] md:block md:h-36 md:w-28", delay: "2.4s", dur: "9.5s" },
            { src: after6,    cls: "left-[28%] bottom-[10%] hidden h-32 w-24 rotate-[10deg] lg:block lg:h-40 lg:w-28", delay: "3s",   dur: "10s" },
            { src: after7,    cls: "right-[28%] top-[28%] hidden h-32 w-24 -rotate-[8deg] lg:block lg:h-40 lg:w-28", delay: "0.9s", dur: "9.7s" },
          ].map((img, i) => (
            <img
              key={i}
              src={img.src}
              alt=""
              aria-hidden="true"
              loading="lazy"
              className={`absolute rounded-xl border-2 border-[#ac8aff]/40 object-cover opacity-90 shadow-[0_0_30px_-4px_rgba(172,138,255,0.7),0_12px_40px_-8px_rgba(0,0,0,0.6)] ring-1 ring-white/10 animate-float-y ${img.cls}`}
              style={{ animationDelay: img.delay, animationDuration: img.dur }}
            />
          ))}

          <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#0e0e11] to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0e0e11] to-transparent" />
        </div>

        <div className="relative mx-auto w-full max-w-6xl px-5 py-20 md:px-8 md:py-28">
          <Reveal className="text-center">
            {/* Anchored heading plate — stays put while the floating images keep animating behind it */}
            <div className="relative mx-auto max-w-2xl rounded-3xl border border-white/10 bg-[#0e0e11]/70 px-6 py-10 backdrop-blur-xl shadow-[0_20px_80px_-20px_rgba(172,138,255,0.35)] md:px-10 md:py-12">
              <div className="pointer-events-none absolute inset-0 rounded-3xl bg-gradient-to-b from-[#ac8aff]/10 via-transparent to-[#00daf3]/10" />
              <div className="relative">
                <div className="relative mx-auto mb-8 flex h-20 w-20 items-center justify-center">
                  <div className="absolute inset-0 animate-ping rounded-full bg-[#ac8aff]/30" />
                  <span className="relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-[#ac8aff] to-[#8455ef] shadow-[0_0_60px_#ac8aff]">
                    <Sparkles className="h-8 w-8 text-[#0e0e11]" strokeWidth={2.5} />
                  </span>
                </div>
                <span
                  className="text-[10px] font-semibold uppercase tracking-[0.4em] text-[#bfa5ff]"
                  style={{ fontFamily: HEADLINE_FONT }}
                >
                  — Voices
                </span>
                <h2
                  className="mt-3 text-4xl font-bold uppercase tracking-tight text-white md:text-5xl"
                  style={{ fontFamily: HEADLINE_FONT }}
                >
                  Sculpting confidence,
                  <br />
                  <span className="bg-gradient-to-r from-[#ac8aff] to-[#00daf3] bg-clip-text text-transparent">
                    one aura at a time
                  </span>
                </h2>
                <p className="mx-auto mt-4 max-w-xl text-sm text-[#acaab1] md:text-base">
                  Real people. Real fits. Real reactions from the FitAura runway.
                </p>
              </div>
            </div>
          </Reveal>

          {/* Infinite right-scrolling marquee reel */}
          <div className="relative mt-14 overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]">
            <div className="flex w-max gap-5 animate-marquee-x hover:[animation-play-state:paused]">
              {[
                ...TESTIMONIALS,
                ...TESTIMONIALS, // duplicate so the loop is seamless
              ].map((r, i) => (
                <figure
                  key={`${r.name}-${i}`}
                  className="group relative flex h-full w-[320px] shrink-0 flex-col rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.05] via-[#19191d]/80 to-white/[0.02] p-6 backdrop-blur-xl transition-all duration-500 hover:-translate-y-1 hover:border-[#ac8aff]/50 hover:shadow-[0_0_50px_-10px_#ac8aff] md:w-[360px]"
                >
                  <div className="pointer-events-none absolute -top-16 left-1/2 h-32 w-32 -translate-x-1/2 rounded-full bg-[#ac8aff]/20 blur-3xl opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  <div className="relative">
                    <div className="flex items-center gap-1 text-[#ac8aff]">
                      {Array.from({ length: r.rating }).map((_, idx) => (
                        <Star key={idx} className="h-4 w-4 fill-[#ac8aff]" strokeWidth={0} />
                      ))}
                    </div>
                    <blockquote className="mt-5 text-sm leading-relaxed text-white/90 md:text-base">
                      "{r.quote}"
                    </blockquote>
                    <figcaption className="mt-6 flex items-center gap-3 border-t border-white/10 pt-5">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#ac8aff] to-[#8455ef] text-sm font-bold text-[#0e0e11]">
                        {r.name.charAt(0)}
                      </span>
                      <div>
                        <p
                          className="text-xs font-bold uppercase tracking-[0.2em] text-white"
                          style={{ fontFamily: HEADLINE_FONT }}
                        >
                          {r.name}
                        </p>
                        <p className="mt-0.5 text-xs text-[#acaab1]">{r.role}</p>
                      </div>
                    </figcaption>
                  </div>
                </figure>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Global Aura — try-ons orbiting the planet */}
      <section className="relative overflow-hidden border-t border-white/5 bg-[#0e0e11]">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#ac8aff]/15 blur-[140px]" />
        </div>
        <div className="mx-auto grid w-full max-w-6xl items-center gap-12 px-5 py-20 md:grid-cols-[1fr_1.1fr] md:gap-16 md:px-8 md:py-28">
          <Reveal>
            <span
              className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#ac8aff]"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              — Worldwide
            </span>
            <h2
              className="mt-3 text-4xl font-bold uppercase leading-[1] tracking-tight text-white md:text-5xl"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              Aura goes
              <br />
              <span className="bg-gradient-to-r from-[#ac8aff] via-[#bfa5ff] to-[#00daf3] bg-clip-text text-transparent">
                global.
              </span>
            </h2>
            <p className="mt-5 max-w-md text-base text-[#acaab1] md:text-lg">
              From Mumbai to Manhattan, real FitAura try-ons are reshaping how people shop. Drag the globe — every
              polaroid is a real generated look from someone, somewhere.
            </p>
            <div
              className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#acaab1]"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              <span className="inline-flex items-center gap-1.5">
                <Check className="h-3.5 w-3.5 text-[#00daf3]" /> 40+ countries
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Check className="h-3.5 w-3.5 text-[#00daf3]" /> 1M+ try-ons
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Cloud className="h-3.5 w-3.5 text-[#bfa5ff]" /> Auto-saved to your phone
              </span>
            </div>
          </Reveal>

          <div className="relative mx-auto w-full max-w-[520px]">
            <GlobePolaroids markers={GLOBE_POLAROIDS} />
            <div className="pointer-events-none absolute left-1/2 top-3 z-10 -translate-x-1/2">
              <LiveViewers />
            </div>
          </div>
        </div>
      </section>

      {/* Scan Process */}

      <section id="how" className="border-t border-white/5">
        <div className="mx-auto w-full max-w-6xl px-5 py-20 md:px-8 md:py-28">
          <Reveal className="text-center">
            <span
              className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#ac8aff]"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              — Scan Lab
            </span>
            <h2
              className="mt-3 text-4xl font-bold uppercase tracking-tight text-white md:text-5xl"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              The Scan Process
            </h2>
          </Reveal>

          <Reveal delay={0.1} className="relative mt-12 rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] p-5 backdrop-blur-xl md:p-8">
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
              {scanProcess.map(({ icon: Icon, n, t, d }) => (
                <div
                  key={n}
                  className="group relative rounded-xl border border-white/10 bg-[#0e0e11]/60 p-5 transition-all hover:border-[#ac8aff]/40 hover:bg-[#19191d]"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#ac8aff]/10 text-[#ac8aff] transition-colors group-hover:bg-[#ac8aff] group-hover:text-[#0e0e11]">
                    <Icon className="h-5 w-5" />
                  </span>
                  <p
                    className="mt-5 text-xs font-bold uppercase tracking-[0.25em] text-[#acaab1]"
                    style={{ fontFamily: HEADLINE_FONT }}
                  >
                    {n}. {t}
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-white/90">{d}</p>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Original 4-step copy preserved as supporting cards */}
          <div className="mt-6 grid gap-3 md:grid-cols-4">
            {[
              { icon: Camera, t: "1. Snap a selfie", d: "Upload a clear, full-body photo or shoot one live." },
              {
                icon: Wand2,
                t: "2. Paste a link",
                d: "Any product from Amazon, Flipkart, Myntra or upload an image.",
              },
              {
                icon: Sparkles,
                t: "3. AI styles you",
                d: "Photo-real try-on in seconds with your face and body preserved.",
              },
              {
                icon: Heart,
                t: "4. Fit & match",
                d: "Get an instant fit score, color harmony and style suggestions.",
              },
            ].map(({ icon: Icon, t, d }) => (
              <div
                key={t}
                className="rounded-lg border border-white/5 bg-white/[0.02] p-4 text-left"
              >
                <div className="flex items-center gap-2 text-[#bfa5ff]">
                  <Icon className="h-4 w-4" />
                  <p
                    className="text-[10px] font-bold uppercase tracking-[0.2em]"
                    style={{ fontFamily: HEADLINE_FONT }}
                  >
                    {t}
                  </p>
                </div>
                <p className="mt-2 text-xs text-[#acaab1]">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Beyond Fitting */}
      <section id="features" className="relative overflow-hidden border-t border-white/5 bg-[#131317]">
        <div className="pointer-events-none absolute -left-40 top-1/2 h-[400px] w-[400px] -translate-y-1/2 rounded-full bg-[#ac8aff]/10 blur-[120px]" />
        <div className="mx-auto grid w-full max-w-6xl gap-12 px-5 py-20 md:grid-cols-2 md:gap-16 md:px-8 md:py-28">
          <Reveal>
            <span
              className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#ac8aff]"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              — Why FitAura
            </span>
            <h2
              className="mt-3 text-5xl font-bold uppercase leading-[0.95] tracking-tight text-white md:text-6xl"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              Beyond
              <br />
              <span className="bg-gradient-to-r from-[#ac8aff] to-[#00daf3] bg-clip-text text-transparent">
                Fitting
              </span>
            </h2>
            <p className="mt-5 max-w-md text-base text-[#acaab1]">
              We aren't just overlaying images. We are rebuilding fashion in three dimensions. Every crease. Every
              fold. Every aura.
            </p>

            <div className="mt-8 space-y-4">
              {[
                {
                  icon: Cpu,
                  t: "Google Nano Banana 2",
                  d: "State-of-the-art Gemini 3.1 Flash Image model — the world's most advanced cloth simulation engine.",
                },
                {
                  icon: Lock,
                  t: "Encrypted Bio-Data",
                  d: "Your photos stay yours. Private storage. We never share or train on your images.",
                },
                {
                  icon: Download,
                  t: "Download & share",
                  d: "Save your try-on photos in HD or share them with friends instantly.",
                },
                {
                  icon: Sparkles,
                  t: "Full outfit styling",
                  d: "AI builds a complete head-to-toe look around any item you try.",
                },
                {
                  icon: ScanFace,
                  t: "Aura Score · 0.10 credits",
                  d: "On-demand editorial read of your vibe, silhouette, color season & statement factor — with tips to elevate.",
                },
              ].map(({ icon: Icon, t, d }) => (
                <div
                  key={t}
                  className="flex gap-4 rounded-xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-sm"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#ac8aff]/10 text-[#ac8aff]">
                    <Icon className="h-5 w-5" />
                  </span>
                  <div>
                    <p
                      className="text-sm font-bold uppercase tracking-[0.15em] text-white"
                      style={{ fontFamily: HEADLINE_FONT }}
                    >
                      {t}
                    </p>
                    <p className="mt-1 text-sm text-[#acaab1]">{d}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Visual panel — Aura Score scanning demo (pure CSS animation, no API) */}
          <Reveal delay={0.15} className="relative flex min-h-[420px] flex-col items-center justify-center overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#ac8aff]/15 via-[#19191d] to-[#00daf3]/10 p-5 backdrop-blur-xl">
            <div className="pointer-events-none absolute inset-0">
              <div
                className="absolute inset-0 opacity-20"
                style={{
                  backgroundImage:
                    "linear-gradient(to right, #ac8aff 1px, transparent 1px), linear-gradient(to bottom, #ac8aff 1px, transparent 1px)",
                  backgroundSize: "40px 40px",
                }}
              />
              <div className="absolute left-1/2 top-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#ac8aff]/30 blur-3xl" />
            </div>

            <span
              className="relative text-[10px] font-bold uppercase tracking-[0.4em] text-[#bfa5ff]"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              — Aura Score · Live Scan
            </span>

            {/* Scanning preview — beam sweeps photo top→bottom */}
            <div className="relative mt-4 w-full max-w-[260px] overflow-hidden rounded-xl border border-[#ac8aff]/40 shadow-[0_0_40px_-8px_#ac8aff]">
              <div className="relative aspect-[3/4] w-full overflow-hidden">
                <img src={after5} alt="Aura scan preview" className="h-full w-full object-cover" />

                {/* Pre-scan dim that lifts as the beam passes (top→bottom wipe reveal) */}
                <div className="aura-reveal pointer-events-none absolute inset-0 bg-[#0e0e11]/55" />

                {/* Soft tint */}
                <div className="absolute inset-0 bg-gradient-to-b from-[#ac8aff]/15 via-transparent to-[#ac8aff]/15" />

                {/* The horizontal scanning beam itself, sweeping top→bottom */}
                <div className="aura-beam pointer-events-none absolute inset-x-0 h-24" />

                {/* Grid shimmer */}
                <div
                  className="pointer-events-none absolute inset-0 opacity-25 mix-blend-overlay"
                  style={{
                    backgroundImage:
                      "linear-gradient(to right, #ac8aff 1px, transparent 1px), linear-gradient(to bottom, #ac8aff 1px, transparent 1px)",
                    backgroundSize: "28px 28px",
                  }}
                />

                {/* Corner brackets */}
                <span className="pointer-events-none absolute left-2 top-2 h-4 w-4 border-l-2 border-t-2 border-[#ac8aff]" />
                <span className="pointer-events-none absolute right-2 top-2 h-4 w-4 border-r-2 border-t-2 border-[#ac8aff]" />
                <span className="pointer-events-none absolute bottom-2 left-2 h-4 w-4 border-b-2 border-l-2 border-[#ac8aff]" />
                <span className="pointer-events-none absolute bottom-2 right-2 h-4 w-4 border-b-2 border-r-2 border-[#ac8aff]" />

                {/* Live description pill — centered over photo, cycles through scan stages */}
                <div className="pointer-events-none absolute inset-x-3 top-1/2 z-10 flex -translate-y-1/2 justify-center">
                  <div className="flex h-8 items-center overflow-hidden rounded-full border border-[#ac8aff]/50 bg-[#0e0e11]/85 px-3.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#bfa5ff] shadow-[0_0_24px_-4px_#ac8aff] backdrop-blur-md">
                    <span className="mr-2 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-[#ac8aff]" />
                    <span className="aura-stage-rotator relative inline-block h-3.5 w-[130px] overflow-hidden">
                      <span className="absolute inset-x-0 top-0 block">Reading silhouette</span>
                      <span className="absolute inset-x-0 top-0 block">Mapping proportions</span>
                      <span className="absolute inset-x-0 top-0 block">Reading color season</span>
                      <span className="absolute inset-x-0 top-0 block">Statement factor</span>
                      <span className="absolute inset-x-0 top-0 block">Photo-readiness</span>
                      <span className="absolute inset-x-0 top-0 block">Composing aura</span>
                    </span>
                  </div>
                </div>

                {/* Floating score ring — number ticks up from 0→92 */}
                <div className="absolute -bottom-4 -right-4 z-10 rounded-full border border-[#ac8aff]/50 bg-[#0e0e11]/90 p-1 shadow-[0_0_30px_-4px_#ac8aff] backdrop-blur-md">
                  <div className="flex h-20 w-20 flex-col items-center justify-center rounded-full bg-gradient-to-br from-[#ac8aff]/20 to-[#00daf3]/10">
                    <span
                      className="aura-counter text-2xl font-bold tabular-nums text-white"
                      style={{ fontFamily: HEADLINE_FONT }}
                    />
                    <span
                      className="text-[8px] font-bold uppercase tracking-[0.2em] text-[#bfa5ff]"
                      style={{ fontFamily: HEADLINE_FONT }}
                    >
                      aura
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Sub-axes — bars fill progressively as scan completes */}
            <div
              className="relative mt-6 grid w-full max-w-[260px] grid-cols-1 gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#acaab1]"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              {[
                { label: "Silhouette",   pct: 88, delay: "0.4s", color: "#ac8aff" },
                { label: "Proportions",  pct: 91, delay: "0.9s", color: "#00daf3" },
                { label: "Color season", pct: 84, delay: "1.4s", color: "#ff84a9" },
                { label: "Statement",    pct: 95, delay: "1.9s", color: "#ac8aff" },
              ].map(({ label, pct, delay, color }) => (
                <div key={label} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span>{label}</span>
                    <span className="tabular-nums text-white" style={{ color }}>{pct}</span>
                  </div>
                  <div className="h-1 w-full overflow-hidden rounded-full bg-white/10">
                    <div
                      className="aura-bar h-full rounded-full"
                      style={{
                        background: `linear-gradient(to right, ${color}, ${color}80)`,
                        ['--bar-target' as string]: `${pct}%`,
                        animationDelay: delay,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>

            <p
              className="relative mt-4 max-w-[260px] text-center text-xs text-[#acaab1]"
            >
              An editorial AI read of your vibe, silhouette &amp; signature style — just{" "}
              <span className="font-semibold text-[#bfa5ff]">0.10 credits</span>.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-t border-white/5">
        <div className="mx-auto w-full max-w-6xl px-5 py-20 md:px-8 md:py-28">
          <Reveal className="text-center">
            <span
              className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#ac8aff]"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              — Collection
            </span>
            <h2
              className="mt-3 text-4xl font-bold uppercase tracking-tight text-white md:text-5xl"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              Pay-as-you-go
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm text-[#acaab1] md:text-base">
              Pay only for what you generate. No subscriptions.{" "}
              <span className="font-semibold text-[#bfa5ff]">Launch offer — 50% OFF</span> all packs.
            </p>
            <div className="mx-auto mt-2 flex max-w-xl flex-col items-center gap-1.5">
              <p className="inline-flex items-center gap-2 rounded-full border border-[#ac8aff]/30 bg-[#ac8aff]/5 px-3 py-1 text-[11px] text-[#bfa5ff]">
                <ScanFace className="h-3 w-3" />
                <span>
                  1 credit = 1 try-on · <span className="font-semibold">Aura Score just 0.10 credits</span> (10× more scans per pack)
                </span>
              </p>
              <p className="inline-flex items-center gap-2 rounded-full border border-[#00daf3]/30 bg-[#00daf3]/5 px-3 py-1 text-[11px] text-[#9be9f5]">
                <Cloud className="h-3 w-3" />
                <span>Every result auto-saved to your private cloud history — never lose a look</span>
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {pricingPacks.map((pack) => (
              <Link
                key={pack.label}
                to="/auth"
                className={
                  pack.popular
                    ? "group relative flex flex-col rounded-2xl border-2 border-[#ac8aff] bg-gradient-to-b from-[#ac8aff]/15 via-[#19191d] to-[#19191d] p-5 shadow-[0_0_50px_-12px_#ac8aff] transition-all hover:-translate-y-1 hover:shadow-[0_0_70px_-10px_#ac8aff] focus:outline-none focus:ring-2 focus:ring-[#ac8aff] focus:ring-offset-2 focus:ring-offset-[#0e0e11]"
                    : "group relative flex flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-sm transition-all hover:-translate-y-1 hover:border-[#ac8aff]/50 hover:bg-white/[0.05] focus:outline-none focus:ring-2 focus:ring-[#ac8aff] focus:ring-offset-2 focus:ring-offset-[#0e0e11]"
                }
              >
                {pack.badge && (
                  <span
                    className={
                      pack.popular
                        ? "absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-gradient-to-r from-[#ac8aff] to-[#8f60fa] px-3 py-1 text-[9px] font-bold uppercase tracking-[0.25em] text-[#0e0e11] shadow-[0_0_20px_-4px_#ac8aff]"
                        : "absolute -top-3 left-4 whitespace-nowrap rounded-full border border-white/15 bg-[#19191d] px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.2em] text-[#bfa5ff]"
                    }
                    style={{ fontFamily: HEADLINE_FONT }}
                  >
                    {pack.badge}
                  </span>
                )}

                <p
                  className={`text-[10px] font-bold uppercase tracking-[0.3em] ${
                    pack.popular ? "text-[#bfa5ff]" : "text-[#acaab1]"
                  }`}
                  style={{ fontFamily: HEADLINE_FONT }}
                >
                  — {pack.label}
                </p>

                <div className="mt-3 flex items-baseline gap-2">
                  <span
                    className="text-4xl font-bold tracking-tight text-white"
                    style={{ fontFamily: HEADLINE_FONT }}
                  >
                    {showInr ? pack.priceInr : pack.priceUsd}
                  </span>
                </div>
                <p className="mt-1 text-[11px] font-medium uppercase tracking-[0.18em] text-[#bfa5ff]/80">
                  {showInr ? pack.perCreditInr : pack.perCreditUsd}
                </p>
                <p className="mt-2 text-xs leading-relaxed text-[#acaab1]">{pack.sub}</p>

                <div className="mt-3 inline-flex items-center gap-1.5 self-start rounded-full border border-[#ac8aff]/25 bg-[#ac8aff]/5 px-2.5 py-1 text-[10px] font-medium text-[#bfa5ff]">
                  <ScanFace className="h-3 w-3" />
                  <span>
                    {pack.credits} try-ons · or up to{" "}
                    <span className="font-bold text-white">{pack.credits * 4}</span> Aura Scores
                  </span>
                </div>

                <div className="my-4 h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />

                <ul className="flex-1 space-y-2 text-[13px] text-white/90">
                  {pack.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#ac8aff]" />
                      <span className="leading-snug">{f}</span>
                    </li>
                  ))}
                </ul>

                <span
                  className={
                    pack.popular
                      ? "mt-5 inline-flex h-11 items-center justify-center rounded-full bg-gradient-to-r from-[#ac8aff] to-[#00daf3] px-4 text-[11px] font-bold uppercase tracking-[0.2em] text-[#0e0e11] shadow-[0_0_25px_-6px_#ac8aff]"
                      : "mt-5 inline-flex h-11 items-center justify-center rounded-full border border-white/15 bg-white/5 px-4 text-[11px] font-bold uppercase tracking-[0.2em] text-white transition-colors group-hover:border-[#ac8aff] group-hover:bg-[#ac8aff] group-hover:text-[#0e0e11]"
                  }
                  style={{ fontFamily: HEADLINE_FONT }}
                >
                  {pack.cta} <ArrowRight className="ml-1.5 h-4 w-4" />
                </span>
              </Link>
            ))}
          </Reveal>

          <p className="mt-10 text-center text-xs text-[#acaab1]">
            Secure global payments · cards, Apple/Google Pay · taxes included
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-white/5 bg-[#131317]">
        <div className="mx-auto w-full max-w-5xl px-5 py-20 md:px-8 md:py-24">
          <Reveal className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#ac8aff]/15 via-[#19191d] to-[#00daf3]/10 p-10 text-center backdrop-blur-xl md:p-16">
            <div className="pointer-events-none absolute -top-32 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-[#ac8aff]/30 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-32 right-0 h-72 w-72 rounded-full bg-[#00daf3]/20 blur-3xl" />
            <div className="relative">
              <span
                className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#bfa5ff]"
                style={{ fontFamily: HEADLINE_FONT }}
              >
                — Find Your Aura
              </span>
              <h3
                className="mt-3 text-3xl font-bold uppercase tracking-tight text-white md:text-5xl"
                style={{ fontFamily: HEADLINE_FONT }}
              >
                Find your FitAura. Free.
              </h3>
              <p className="mx-auto mt-4 max-w-md text-sm text-[#acaab1] md:text-base">
                Your first 3 try-ons are on us. No credit card required.
              </p>
              <Button
                asChild
                size="lg"
                className="mt-8 h-12 rounded-full bg-gradient-to-r from-[#ac8aff] to-[#8f60fa] px-8 text-xs font-bold uppercase tracking-[0.25em] text-[#0e0e11] shadow-[0_0_30px_-6px_#ac8aff] hover:from-[#bfa5ff] hover:to-[#ac8aff]"
                style={{ fontFamily: HEADLINE_FONT }}
              >
                <Link to="/auth">
                  Start Free <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 bg-[#0e0e11]">
        <div className="mx-auto w-full max-w-6xl px-5 py-14 md:px-8">
          <div className="grid gap-10 md:grid-cols-4">
            <div className="md:col-span-1">
              <div className="flex items-center gap-2.5">
                <span className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-lg">
                  <img
                    src="/app-icon-128.png"
                    alt="FitAura AI logo"
                    className="h-full w-full object-cover"
                    width={32}
                    height={32}
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                  />
                </span>
                <span
                  className="text-base font-bold uppercase tracking-[0.18em] text-white"
                  style={{ fontFamily: HEADLINE_FONT }}
                >
                  FitAura AI
                </span>
              </div>
              <p className="mt-4 text-sm text-[#acaab1]">
                Revolutionizing the digital runway since 2024. Sculpting light into pure style.
              </p>
            </div>

            <div>
              <p
                className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#bfa5ff]"
                style={{ fontFamily: HEADLINE_FONT }}
              >
                Product
              </p>
              <ul className="mt-4 space-y-2.5 text-sm text-[#acaab1]">
                <li>
                  <a href="#examples" className="transition-colors hover:text-white">
                    Runway
                  </a>
                </li>
                <li>
                  <a href="#how" className="transition-colors hover:text-white">
                    Scanner
                  </a>
                </li>
                <li>
                  <a href="#pricing" className="transition-colors hover:text-white">
                    Pricing
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <p
                className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#bfa5ff]"
                style={{ fontFamily: HEADLINE_FONT }}
              >
                Company
              </p>
              <ul className="mt-4 space-y-2.5 text-sm text-[#acaab1]">
                <li>
                  <Link to="/about" className="transition-colors hover:text-white">
                    About
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="transition-colors hover:text-white">
                    Contact
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="transition-colors hover:text-white">
                    Privacy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="transition-colors hover:text-white">
                    Terms
                  </Link>
                </li>
                <li>
                  <Link to="/refund" className="transition-colors hover:text-white">
                    Refund
                  </Link>
                </li>
              </ul>
            </div>

          </div>

          <div className="mt-12 flex flex-col items-start justify-between gap-3 border-t border-white/5 pt-6 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#76757b] md:flex-row md:items-center">
            <span style={{ fontFamily: HEADLINE_FONT }}>
              © {new Date().getFullYear()} FitAura AI Labs. All Rights Reserved.
            </span>
            <span style={{ fontFamily: HEADLINE_FONT }}>Built for the high-velocity aesthetic.</span>
          </div>
        </div>
      </footer>

      {embedded && (
        <div className="sticky bottom-0 left-0 right-0 z-40 border-t border-white/10 bg-[#0e0e11]/95 backdrop-blur-xl pb-[env(safe-area-inset-bottom)]">
          <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-3 px-5 py-3 md:px-8">
            <span
              className="hidden text-[10px] font-bold uppercase tracking-[0.25em] text-[#bfa5ff] sm:inline"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              You're in. Let's set up your aura.
            </span>
            <Button
              onClick={onContinue}
              size="lg"
              className="ml-auto rounded-full bg-gradient-to-r from-[#ac8aff] to-[#8f60fa] px-7 text-xs font-bold uppercase tracking-[0.2em] text-[#0e0e11] shadow-[0_0_24px_-4px_#ac8aff] hover:from-[#bfa5ff] hover:to-[#ac8aff]"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              Continue to Setup
              <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2.5} />
            </Button>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default Index;
